// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

import {
	Alert,
	Button,
	DropZone,
	EmptyState,
	Fcd,
	FileButton,
	Grid,
	IcOffline,
	Icon,
	IcUpload,
	List,
	LoadingSpinner,
	LoadMoreTrigger,
	Text,
	useAuth,
	useDebouncedValue,
	useDialog,
	useIsMobile,
	useToast,
	VBox
} from '@cloudillo/react'
import { ROLE_LEVELS, roleLevel } from '@cloudillo/types'
import { useAtomValue, useSetAtom } from 'jotai'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
	useActiveCommunity,
	useContextAwareApi,
	useContextRolesFor,
	useCtx,
	useCurrentContextIdTag
} from '../../context/index.js'
import { IcCloud } from '../../icons.js'
import { usePorch } from '../../lib/porch.js'
import { getDirtyDocIds } from '../../message-bus/handlers/crdt.js'
import { appPath, type QueryInit } from '../../routes.js'
import { isPermissionError, useAppConfig } from '../../utils.js'
import { displayModeAtom, lastFilesUrlAtom, roomLabel } from './atoms.js'
import { audienceText, confirmAudienceDialog, widensAudience } from './audience.js'
import type { ContextMenuPosition } from './components/index.js'
import {
	ContextMenu,
	CreateMenu,
	DetailsPanel,
	FilesBar,
	FilterBar,
	HandActionBar,
	ImportChoiceDialog,
	ItemCard,
	ItemGrid,
	ShareDialog,
	Sidebar,
	UploadProgress
} from './components/index.js'
import { useCurrentFolder } from './hooks/useCurrentFolder.js'
import {
	convertFileView,
	useFileList,
	useFileNavigation,
	useKeyboardShortcuts,
	useMultiSelect,
	useSmartUpload
} from './hooks/index.js'
import { useFilesFilters } from './hooks/useFilesFilters.js'
import type { File, FileOps, ViewMode } from './types.js'
import { isFileProcessing } from './types.js'
import { canWrite, fileSrcIdTag } from './utils.js'

export function FilesApp() {
	const navigate = useNavigate()
	const location = useLocation()
	const setLastFilesUrl = useSetAtom(lastFilesUrlAtom)
	React.useEffect(
		function rememberLocation() {
			setLastFilesUrl({ pathname: location.pathname, search: location.search })
		},
		[location.pathname, location.search, setLastFilesUrl]
	)
	const { t } = useTranslation()
	const [appConfig] = useAppConfig()
	const { api } = useContextAwareApi()
	const [auth] = useAuth()
	const contextIdTag = useCurrentContextIdTag()
	const community = useActiveCommunity()
	const { base: urlContextIdTag, isHome } = useCtx()
	const dialog = useDialog()
	const toast = useToast()

	// Navigation state
	const {
		currentFolderId,
		remoteOwner,
		shareRoot,
		isRemoteBrowsing,
		room,
		roomChannel,
		contextName,
		remoteAccessLevel,
		remoteApi,
		remoteRoles,
		breadcrumbs,
		viewMode,
		navigateToFolder,
		navigateToView,
		navigateToRoom,
		goUp,
		enterFolder
	} = useFileNavigation()
	const ctxRoles = useContextRolesFor(contextIdTag)
	// The whole porch: restore tells a deleted room (absent) from one merely left
	const { rooms: porch } = usePorch(isRemoteBrowsing ? undefined : contextIdTag, ctxRoles)
	const rooms = React.useMemo(() => (porch ?? []).filter((r) => r.status === 'in'), [porch])
	// Porch entry of the current room; undefined for a room the reader is not `in`
	const currentRoom = room ? rooms.find((r) => r.name === room) : undefined
	// A `?room=` naming no room we are in (stale link, left room): fall back to the main room
	const unknownRoom = !!porch && !!room && !currentRoom
	React.useEffect(() => {
		if (unknownRoom) navigate({ search: '' }, { replace: true })
	}, [unknownRoom, navigate])
	// The place's root: `~name · Title` for a room, else the context's name
	const roomTitle = roomLabel(room, contextName, currentRoom?.title)
	// What room scope covers: the room, or the share root while remote browsing
	const scopeRoot = isRemoteBrowsing ? (breadcrumbs[0]?.name ?? roomTitle) : roomTitle

	const audience = isRemoteBrowsing
		? undefined
		: audienceText(t, contextName, room ? currentRoom : null, isHome)
	// Room header (title + audience) while browsing a local room
	const isRoomView = viewMode === 'browse' && !isRemoteBrowsing
	// Home needs no role (own session); a community needs contributor, as the server's create check.
	const canCreate = isRemoteBrowsing
		? canWrite(remoteAccessLevel)
		: !contextIdTag ||
			contextIdTag === auth?.idTag ||
			roleLevel(ctxRoles) >= ROLE_LEVELS.contributor

	// Dirty (unsynced) CRDT documents
	const [dirtyDocIds, setDirtyDocIds] = React.useState<Set<string>>(new Set())
	React.useEffect(() => {
		getDirtyDocIds().then(setDirtyDocIds)
	}, [])

	// Filter state lives in the URL
	const { filters, effectiveScope, activeCount, setFilter, setScope, clearAll, toListQuery } =
		useFilesFilters()
	const searchActive = activeCount > 0
	// Parent-folder chip only adds info when rows can escape the current folder: Trash/Managed,
	// or a room/all-scope listing. In folder scope every row shares the crumb's parent.
	const showParentChip = viewMode !== 'browse' || effectiveScope !== 'folder'

	// File list (with all filters)
	const listQuery = toListQuery({
		viewMode,
		parentId: currentFolderId,
		channel: isRemoteBrowsing ? undefined : (roomChannel ?? ''),
		shareRoot,
		ownerIdTag: contextIdTag
	})
	const fileListData = useFileList({
		query: listQuery,
		perm: filters.perm,
		permRoles: isRemoteBrowsing ? remoteRoles : ctxRoles,
		remoteApi
	})

	// Bumped on every star toggle or rename/trash/restore so the sidebar refetches its starred folders
	const [starVersion, setStarVersion] = React.useState(0)

	// The remote node, the tenant it belongs to, and our roles there — derived once and handed to
	// both the details panel and the share dialog it opens, so the affordance one offers and what
	// the other allows are decided from the same values.
	const remoteScope = React.useMemo(
		() =>
			isRemoteBrowsing
				? {
						api: remoteApi,
						idTag: remoteOwner ?? undefined,
						roles: remoteRoles,
						// The row is the remote node's whether or not its token has landed. Dropping
						// the override meanwhile re-judges it against the LOCAL context, whose
						// roles say nothing about standing on the node being browsed.
						resolving: !remoteApi
					}
				: undefined,
		[isRemoteBrowsing, remoteApi, remoteOwner, remoteRoles]
	)

	// Smart upload (wraps upload queue with import detection)
	const uploadQueue = useSmartUpload({
		parentId: currentFolderId,
		onUploadComplete: fileListData.refresh,
		apiOverride: isRemoteBrowsing ? remoteApi : undefined,
		// Channel of the current room; the hooks send it only at the root (folders inherit)
		channel: isRemoteBrowsing ? undefined : roomChannel
	})

	// Clear finished uploads so the mobile FAB returns; errors stay so failures remain visible.
	React.useEffect(() => {
		if (uploadQueue.stats.pending > 0 || uploadQueue.stats.completed === 0) return
		const timer = setTimeout(uploadQueue.clearCompleted, 3000)
		return () => clearTimeout(timer)
	}, [uploadQueue.stats.pending, uploadQueue.stats.completed, uploadQueue.clearCompleted])

	// Sort files: pinned first (except in Recent/Trash), then folders, then regular files
	const files = React.useMemo(() => {
		const data = fileListData.getData()
		const skipPinSort = filters.recent || viewMode === 'trash' || viewMode === 'managed'
		return [...data].sort((a, b) => {
			// Pinned files first (not in Recent or Trash views)
			if (!skipPinSort) {
				const aPinned = a.userData?.pinned ? 1 : 0
				const bPinned = b.userData?.pinned ? 1 : 0
				if (aPinned !== bPinned) return bPinned - aPinned
			}

			// Folders second
			const aFolder = a.fileTp === 'FLDR' ? 1 : 0
			const bFolder = b.fileTp === 'FLDR' ? 1 : 0
			if (aFolder !== bFolder) return bFolder - aFolder

			// Default: keep original order (from API)
			return 0
		})
	}, [fileListData, viewMode, filters.recent])

	// A metadata edit changes a row that is already loaded, so patch it. refresh() resets
	// useInfiniteScroll: it blanks the list, drops every page past the first and the scroll
	// position with them. Only a row appearing or disappearing is worth that.
	const patchFile = React.useCallback(
		function patchFile(
			entryId: string,
			patch: Partial<File> | ((file: File) => Partial<File>)
		) {
			if (!fileListData.getData().some((f) => f.entryId === entryId)) {
				// Not in this list — only a refetch can show what changed.
				fileListData.refresh()
				return
			}
			// Merged against the CURRENT row inside the updater, not against the render-time
			// snapshot: a FILE_ID_GENERATED or fileViewUpdateAtom patch landing while the API
			// call was in flight would otherwise be rolled back.
			fileListData.setFileData(entryId, (current) => ({
				...current,
				...(typeof patch === 'function' ? patch(current) : patch)
			}))
		},
		[fileListData]
	)

	// Multi-select state
	const multiSelect = useMultiSelect({
		files: files,
		// Place and filters (all in the URL): `pruneOnFilesChange` bails on an empty list, so
		// filtering down to zero results would otherwise preserve the old selection invisibly.
		resetKey: location.search
	})

	// Get the first selected file for the details panel
	const selectedFile = multiSelect.getFirstSelected()

	// Debounce selected file for the details panel to avoid re-rendering during rapid keyboard navigation
	const detailsFile = useDebouncedValue(selectedFile, 300)

	// Rename state
	const [renameFileId, setRenameFileId] = React.useState<string | undefined>()
	const [renameFileName, setRenameFileName] = React.useState<string | undefined>()

	// Filter visibility (mobile)
	const [showFilter, setShowFilter] = React.useState<boolean>(false)

	// Display mode (grid/list, persists via localStorage); toggled in the FilterBar
	const displayMode = useAtomValue(displayModeAtom)

	// Context menu state
	const [contextMenuFile, setContextMenuFile] = React.useState<File | undefined>()
	const [contextMenuPosition, setContextMenuPosition] = React.useState<
		ContextMenuPosition | undefined
	>()

	// Share dialog state
	const [shareDialogFile, setShareDialogFile] = React.useState<File | undefined>()

	// Mobile details panel visibility (separate from selection)
	// On desktop (>=768px), details panel follows selection
	// On mobile (<768px), details panel only shows when explicitly requested via info button
	const [showMobileDetails, setShowMobileDetails] = React.useState<boolean>(false)
	const isMobile = useIsMobile()

	// The folder being browsed, for the bar's Share / ⓘ; ⓘ shows it when nothing is selected
	const currentFolder = useCurrentFolder(currentFolderId, isRemoteBrowsing ? remoteApi : api)
	const [showFolderDetails, setShowFolderDetails] = React.useState(false)
	React.useEffect(() => setShowFolderDetails(false), [currentFolderId])
	const panelFile = selectedFile ?? (showFolderDetails ? currentFolder : undefined)
	const shownFile = detailsFile ?? panelFile

	// Reset filter on path change (not on search param changes like folder navigation)
	React.useEffect(
		function onLocationEffect() {
			setShowFilter(false)
		},
		[location.pathname]
	)

	const onClickFile = React.useCallback(
		function onClickFile(file: File, event: React.MouseEvent) {
			multiSelect.handleClick(file, event)
		},
		[multiSelect]
	)

	const onDoubleClickFile = React.useCallback(
		function onDoubleClickFile(file: File) {
			if (file.fileTp === 'FLDR') {
				enterFolder(file)
			}
		},
		[enterFolder]
	)

	const onContextMenuFile = React.useCallback(
		function onContextMenuFile(file: File, position: ContextMenuPosition) {
			// If clicked file is not already selected, make it the only selection
			// This ensures context menu operations target the right file(s)
			if (!multiSelect.isSelected(file.entryId)) {
				multiSelect.handleClick(file, {
					ctrlKey: false,
					metaKey: false,
					shiftKey: false
				} as React.MouseEvent)
			}
			setContextMenuFile(file)
			setContextMenuPosition(position)
		},
		[multiSelect]
	)

	const closeContextMenu = React.useCallback(function closeContextMenu() {
		setContextMenuFile(undefined)
		setContextMenuPosition(undefined)
	}, [])

	// Info button click handler (mobile only) - selects the file and shows details panel
	const onInfoClick = React.useCallback(
		function onInfoClick(file: File) {
			multiSelect.handleClick(file, {
				ctrlKey: false,
				metaKey: false,
				shiftKey: false
			} as React.MouseEvent)
			setShowMobileDetails(true)
		},
		[multiSelect]
	)

	const clearSelectionRef = React.useRef(multiSelect.clearSelection)
	clearSelectionRef.current = multiSelect.clearSelection

	// Sidebar items always clear the filters; the navigate* helpers no-op on a re-click of the
	// current place, so that case clears in place instead.
	const handleViewModeChange = React.useCallback(
		function (mode: ViewMode) {
			if (mode === viewMode) clearAll()
			else navigateToView(mode)
			clearSelectionRef.current()
		},
		[viewMode, navigateToView, clearAll]
	)

	const handleRoomChange = React.useCallback(
		function (name: string | null) {
			if (viewMode === 'browse' && !currentFolderId && !remoteOwner && room === name) {
				clearAll()
			} else navigateToRoom(name)
		},
		[viewMode, currentFolderId, remoteOwner, room, navigateToRoom, clearAll]
	)

	const handleCreateFolder = React.useCallback(
		async function () {
			if (!api) return

			const folderName = await dialog.askText(
				t('Create folder'),
				t('Provide a name for the new folder'),
				{ placeholder: t('Untitled folder') }
			)
			if (folderName === undefined) return

			try {
				await api.files.create({
					fileTp: 'FLDR',
					contentType: 'cloudillo/folder',
					fileName: folderName || t('Untitled folder'),
					parentId: currentFolderId || undefined,
					channel: currentFolderId ? undefined : roomChannel
				})
				fileListData.refresh()
			} catch (err) {
				console.error('[Files] create folder failed', err)
				toast.error(t('Failed to create folder'))
			}
		},
		[api, dialog, t, toast, currentFolderId, roomChannel, fileListData]
	)

	const handleEmptyTrash = React.useCallback(
		async function () {
			if (!api) return

			const res = await dialog.confirm(
				t('Empty trash'),
				t(
					'Are you sure you want to permanently delete all files in trash? This action cannot be undone.'
				),
				{ color: 'error', confirmLabel: t('Empty trash') }
			)
			if (!res) return

			await api.trash.empty()
			fileListData.refresh()
		},
		[api, dialog, t, fileListData]
	)

	const fileOps: FileOps = React.useMemo(() => {
		// A restore lands at the root of the entry's room; when that room is gone (absent
		// from the porch, not merely left) it falls back to the main room — a wider audience.
		async function confirmRestore(entryIds: string[]): Promise<boolean> {
			const data = fileListData.getData()
			const orphaned = entryIds.some((id) => {
				const ch = data.find((f) => f.entryId === id)?.channel
				return widensAudience(porch, contextIdTag ?? '', ch, ch ?? null)
			})
			if (!orphaned) return true
			return confirmAudienceDialog(
				dialog,
				t,
				t('Restore to {{place}}?', { place: contextName }),
				audienceText(t, contextName, null, isHome) ?? contextName
			)
		}

		// Shared navigation helper for opening files in apps
		function navigateToFile(
			file: File,
			appName: string,
			access?: 'read' | 'comment' | 'write',
			params?: string
		) {
			if (!file.fileId) return // local folders have no content id, so no resId
			// `contextIdTag` is still OUR context while remote-browsing — that is a query
			// param, not a context switch — so the browsed node has to be passed explicitly.
			const srcIdTag = fileSrcIdTag(file, {
				remoteOwner: isRemoteBrowsing ? remoteOwner : undefined,
				contextIdTag,
				authIdTag: auth?.idTag
			})
			const query: QueryInit = {}
			if (access && access !== 'write') query.access = access
			if (params) {
				for (const [k, v] of new URLSearchParams(params)) {
					query[k] = v
				}
			}
			navigate(appPath(urlContextIdTag, appName, `${srcIdTag}:${file.fileId}`, query))
		}

		return {
			setFile: function setFile(file: File) {
				fileListData.setFileData(file.entryId, file)
			},

			openFile: function openFile(entryId?: string, access?: 'read' | 'comment' | 'write') {
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				if (!file) return
				if (isFileProcessing(file)) {
					toast.warning(
						t('This file is still being processed — please try again in a moment.')
					)
					return
				}
				const app = appConfig?.mime[file.contentType]
				if (app) {
					const appName = app.split('/').pop()!
					navigateToFile(file, appName, access)
				} else if (!file.fileTp || file.fileTp === 'BLOB') {
					// Unknown binary: open the built-in viewer, which shows a Download fallback
					navigateToFile(file, 'view', access)
				} else {
					// CRDT/RTDB docs with no handler: leave as-is (handled elsewhere as
					// "Unsupported file type")
					console.warn('[FilesApp] openFile: no app for', file.contentType, file.fileTp)
				}
			},

			openFileWithApp: function openFileWithApp(
				entryId: string,
				appId: string,
				access?: 'read' | 'comment' | 'write',
				params?: string
			) {
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				if (file) {
					navigateToFile(file, appId, access, params)
				} else {
					console.warn('[FilesApp] File not found in data:', entryId)
				}
			},

			renameFile: function renameFile(entryId?: string) {
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				setRenameFileId(entryId)
				setRenameFileName(file?.fileName || '')
			},

			setRenameFileName,

			doRenameFile: async function doRenameFile(entryId: string, fileName: string) {
				if (!api) return
				// Unhandled, a 403 here rejects into nothing: the dialog sits open with no
				// explanation and the name silently does not change. Kept open on failure on
				// purpose, so the user can retry or cancel.
				try {
					await api.files.update(entryId, { fileName })
					setRenameFileId(undefined)
					setRenameFileName(undefined)
					patchFile(entryId, { fileName })
					setStarVersion((v) => v + 1)
				} catch (err) {
					console.error('Failed to rename file', err)
					toast.error(
						isPermissionError(err)
							? t('You do not have permission to rename this file.')
							: t('Failed to rename file')
					)
				}
			},

			doDeleteFile: async function doDeleteFile(entryId: string) {
				if (!api) return
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				const isFolder = file?.fileTp === 'FLDR'

				// Owner-only: probe share entries to warn about active embeds / share links.
				// Per PRD non-goal, user/community grants ('U') are excluded — revocation cascades naturally.
				let warning: string | null = null
				if (file && file.owner?.idTag === auth?.idTag) {
					try {
						const shares = await api.files.listShares(entryId)
						const list = Array.isArray(shares) ? shares : []
						const embeds = list.filter((e) => e.subjectType === 'F').length
						const links = list.filter(
							(e) => e.subjectType !== 'F' && e.subjectType !== 'U'
						).length
						if (embeds > 0 || links > 0) {
							warning = t(
								'This file is currently used by:\n- {{links}} share link(s)\n- {{embeds}} embed(s)\n\nTrashing it will break those references.',
								{ links, embeds }
							)
						}
					} catch {
						// best-effort; skip warning on probe failure
					}
				}

				const res = await dialog.confirm(
					warning
						? t('Move "{{name}}" to trash?', { name: file?.fileName ?? '' })
						: isFolder
							? t('Move folder to trash')
							: t('Move to trash'),
					warning
						? warning
						: isFolder
							? t(
									'Are you sure you want to move this folder and its contents to trash?'
								)
							: t('Are you sure you want to move this file to trash?'),
					{ color: 'error', confirmLabel: t('Move to trash') }
				)
				if (!res) return

				await api.files.delete(entryId)
				setStarVersion((v) => v + 1)
				if (multiSelect.isSelected(entryId)) {
					multiSelect.clearSelection()
				}
				fileListData.refresh()
			},

			doRestoreFile: async function doRestoreFile(entryId: string, parentId?: string) {
				if (!api) return
				if (!parentId && !(await confirmRestore([entryId]))) return
				await api.files.restore(entryId, parentId)
				setStarVersion((v) => v + 1)
				fileListData.refresh()
			},

			doPermanentDeleteFile: async function doPermanentDeleteFile(entryId: string) {
				if (!api) return
				const res = await dialog.confirm(
					t('Permanently delete'),
					t(
						'Are you sure you want to permanently delete this file? This action cannot be undone.'
					),
					{ color: 'error', confirmLabel: t('Delete') }
				)
				if (!res) return

				await api.files.permanentDelete(entryId)
				setStarVersion((v) => v + 1)
				if (multiSelect.isSelected(entryId)) {
					multiSelect.clearSelection()
				}
				fileListData.refresh()
			},

			toggleStarred: async function toggleStarred(entryId: string) {
				if (!api) return
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				const starred = !(file?.userData?.starred ?? false)
				try {
					await api.files.setStarred(entryId, starred)
				} catch (err) {
					console.error('Failed to change starred state', err)
					toast.error(t('Failed to change starred state'))
					return
				}
				// Un-starring in the starred view removes the row: only a refetch can do that.
				if (filters.starred && !starred) fileListData.refresh()
				else patchFile(entryId, (f) => ({ userData: { ...f.userData, starred } }))
				setStarVersion((v) => v + 1)
			},

			togglePinned: async function togglePinned(entryId: string) {
				if (!api) return
				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				const pinned = !(file?.userData?.pinned ?? false)
				try {
					await api.files.setPinned(entryId, pinned)
				} catch (err) {
					console.error('Failed to change pinned state', err)
					toast.error(t('Failed to change pinned state'))
					return
				}
				patchFile(entryId, (f) => ({ userData: { ...f.userData, pinned } }))
			},

			// Batch operations for multi-select
			doDeleteFiles: async function doDeleteFiles(entryIds: string[]) {
				if (!api || entryIds.length === 0) return

				const res = await dialog.confirm(
					t('Move to trash'),
					t('Are you sure you want to move {{count}} items to trash?', {
						count: entryIds.length
					}),
					{ color: 'error', confirmLabel: t('Move to trash') }
				)
				if (!res) return

				await Promise.all(entryIds.map((id) => api.files.delete(id)))
				setStarVersion((v) => v + 1)
				multiSelect.clearSelection()
				fileListData.refresh()
			},

			doRestoreFiles: async function doRestoreFiles(entryIds: string[], parentId?: string) {
				if (!api || entryIds.length === 0) return
				if (!parentId && !(await confirmRestore(entryIds))) return
				await Promise.all(entryIds.map((id) => api.files.restore(id, parentId)))
				setStarVersion((v) => v + 1)
				multiSelect.clearSelection()
				fileListData.refresh()
			},

			doPermanentDeleteFiles: async function doPermanentDeleteFiles(entryIds: string[]) {
				if (!api || entryIds.length === 0) return

				const res = await dialog.confirm(
					t('Permanently delete'),
					t(
						'Are you sure you want to permanently delete {{count}} items? This action cannot be undone.',
						{ count: entryIds.length }
					),
					{ color: 'error', confirmLabel: t('Delete') }
				)
				if (!res) return

				await Promise.all(entryIds.map((id) => api.files.permanentDelete(id)))
				setStarVersion((v) => v + 1)
				multiSelect.clearSelection()
				fileListData.refresh()
			},

			toggleStarredBatch: async function toggleStarredBatch(
				entryIds: string[],
				starred: boolean
			) {
				if (!api || entryIds.length === 0) return
				// Per-id outcomes: `Promise.all` would abandon the rows that DID succeed on the
				// server, leaving the list disagreeing with the backend until a refresh.
				const results = await Promise.allSettled(
					entryIds.map((id) => api.files.setStarred(id, starred))
				)
				const done = entryIds.filter((_, i) => results[i].status === 'fulfilled')
				// Nothing succeeded on the server, so nothing here has to change.
				if (done.length > 0) {
					setStarVersion((v) => v + 1)
					if (filters.starred && !starred) {
						// Un-starring in the starred view removes the rows: only a refetch
						// can do that.
						fileListData.refresh()
					} else {
						for (const id of done) {
							patchFile(id, (f) => ({ userData: { ...f.userData, starred } }))
						}
					}
				}
				if (done.length < entryIds.length) {
					console.error(
						'Failed to change starred state for some files',
						results.filter((r) => r.status === 'rejected')
					)
					toast.error(t('Failed to change starred state'))
				}
			},

			togglePinnedBatch: async function togglePinnedBatch(
				entryIds: string[],
				pinned: boolean
			) {
				if (!api || entryIds.length === 0) return
				const results = await Promise.allSettled(
					entryIds.map((id) => api.files.setPinned(id, pinned))
				)
				const done = entryIds.filter((_, i) => results[i].status === 'fulfilled')
				for (const id of done) {
					patchFile(id, (f) => ({ userData: { ...f.userData, pinned } }))
				}
				if (done.length < entryIds.length) {
					console.error(
						'Failed to change pinned state for some files',
						results.filter((r) => r.status === 'rejected')
					)
					toast.error(t('Failed to change pinned state'))
				}
			},

			// `scopedApi` is the file's own node when the caller knows it differs from ours - see
			// FileOps.setVisibility. Without it a remote row's update goes to the wrong server.
			setVisibility: async function setVisibility(entryId: string, visibility, scopedApi) {
				const target = scopedApi ?? api
				if (!target) return
				// Called without an await from the details panel and the context menu, and the
				// whole point of `scopedApi` is a foreign node - which is exactly where a 403
				// happens. Unhandled, the dropdown just closes and nothing changes.
				try {
					await target.files.update(entryId, { visibility })
					// Only our own node's row is the one this list renders. A `scopedApi` write
					// changed the canonical copy upstream and left the local mirror alone, so
					// patching it here would show a value the next listing contradicts.
					if (target === api) patchFile(entryId, { visibility })
					else fileListData.refresh()
				} catch (err) {
					console.error('Failed to change visibility', err)
					toast.error(
						isPermissionError(err)
							? t('You do not have permission to change this file’s visibility.')
							: t('Failed to change visibility')
					)
				}
			},

			doDuplicateFile: async function doDuplicateFile(entryId: string) {
				if (!api) return

				const file = fileListData.getData()?.find((f) => f.entryId === entryId)
				const defaultName = t('Copy of {{name}}', { name: file?.fileName || '' })

				const fileName = await dialog.askText(
					t('Duplicate'),
					t('Provide a name for the duplicate'),
					{ defaultValue: defaultName }
				)
				if (fileName === undefined) return

				try {
					await api.files.duplicate(entryId, { fileName: fileName || defaultName })
					toast.success(t('File duplicated'))
					fileListData.refresh()
				} catch (err) {
					console.error('Failed to duplicate file', err)
					toast.error(t('Failed to duplicate file'))
				}
			},

			doRefreshFile: async function doRefreshFile(entryId: string) {
				if (!api) return
				try {
					const res = await api.files.refresh(entryId)
					// `unreachable` means the source never answered: the row came back
					// untouched, so there is nothing to patch and nothing to celebrate.
					if (res.refreshStatus === 'unreachable') {
						toast.error(
							t("{{host}} couldn't be reached. We'll keep trying.", {
								host: res.upstream?.idTag ?? res.owner?.idTag ?? ''
							})
						)
						return
					}
					toast.success(t('File metadata refreshed'))
					const r = convertFileView(res)
					// Only what POST /files/:id/refresh reconciles. A blanket spread would
					// overwrite every field `convertFileView` materialises unconditionally —
					// userData above all, which this response does not carry.
					patchFile(entryId, {
						fileName: r.fileName,
						contentType: r.contentType,
						fileTp: r.fileTp,
						tags: r.tags,
						preset: r.preset,
						// Only when the response actually carries one: it is the per-user
						// CACHED level, NULL on a pre-v30 row, while the listing always
						// computes one (`compute_file_access_levels`). Spreading `undefined`
						// here strips the row's Share/visibility affordances until a refetch.
						...(r.accessLevel !== undefined ? { accessLevel: r.accessLevel } : {}),
						brokenAt: r.brokenAt,
						brokenReason: r.brokenReason
					})
				} catch (err) {
					console.error('Failed to refresh file metadata', err)
					toast.error(t('Failed to refresh file metadata'))
				}
			}
		}
	}, [
		auth,
		api,
		appConfig,
		contextIdTag,
		isRemoteBrowsing,
		remoteOwner,
		urlContextIdTag,
		navigate,
		t,
		fileListData,
		patchFile,
		viewMode,
		dialog,
		toast,
		multiSelect,
		porch,
		contextName,
		isHome,
		filters.starred
	])

	// Keyboard shortcuts
	useKeyboardShortcuts({
		files: files,
		selectedFile,
		onSelectFile: (file) =>
			file
				? multiSelect.handleClick(file, {
						ctrlKey: false,
						metaKey: false,
						shiftKey: false
					} as React.MouseEvent)
				: multiSelect.clearSelection(),
		onEnterFolder: enterFolder,
		onGoToParent: goUp,
		fileOps,
		isRenaming: renameFileId !== undefined,
		onSelectAll: multiSelect.selectAll
	})

	const isTrashView = viewMode === 'trash'
	const isInitialLoading = fileListData.isLoading && files.length === 0

	// The perm filter stops auto-loading after a few emptied pages; say so instead of spinning
	const loadMoreFooter = fileListData.permCapped ? (
		<VBox gap={2} align="center" className="p-2">
			<Text size="sm" emphasis="muted">
				{t('Some files may be hidden by ‘My permission’.')}
			</Text>
			<Button size="sm" onClick={fileListData.loadMore}>
				{t('Load more')}
			</Button>
		</VBox>
	) : (
		<LoadMoreTrigger
			ref={fileListData.sentinelRef}
			isLoading={fileListData.isLoadingMore}
			hasMore={fileListData.hasMore}
			error={fileListData.error}
			onRetry={fileListData.loadMore}
			loadingLabel={t('Loading more files...')}
			retryLabel={t('Retry')}
			errorPrefix={t('Failed to load:')}
		/>
	)

	// Disable drag-drop in trash view and read-only remote browsing
	const canUpload = viewMode === 'browse' && canCreate
	const createMenuProps = {
		contextIdTag,
		currentFolderId,
		channel: isRemoteBrowsing ? undefined : roomChannel,
		onCreateFolder:
			viewMode === 'browse' && !isRemoteBrowsing && canCreate
				? handleCreateFolder
				: undefined,
		onUpload: canUpload ? uploadQueue.handleFilesForUpload : undefined
	}

	return (
		<>
			<DropZone
				variant="overlay"
				target="viewport"
				className="h-100"
				onFiles={canUpload ? uploadQueue.handleFilesForUpload : () => {}}
				hover={
					canUpload ? (
						<VBox gap={2} align="center">
							<Icon as={IcUpload} size="xl" />
							<Text>{t('Drop files here to upload')}</Text>
						</VBox>
					) : undefined
				}
			>
				<Fcd.Container
					bar={
						<FilesBar
							viewMode={viewMode}
							effectiveScope={effectiveScope}
							roomTitle={roomTitle}
							breadcrumbs={breadcrumbs}
							onNavigate={navigateToFolder}
							remote={
								isRemoteBrowsing
									? {
											ownerIdTag: remoteOwner ?? undefined,
											permission: canWrite(remoteAccessLevel)
												? 'write'
												: 'read'
										}
									: undefined
							}
							currentFolder={currentFolder}
							folderId={currentFolderId}
							ownerScope={remoteScope}
							slotFallback={
								!isRemoteBrowsing && audience
									? {
											idTag:
												community?.idTag ??
												contextIdTag ??
												auth?.idTag ??
												'',
											label: audience,
											ownerOnly: isHome && !room
										}
									: undefined
							}
							onShare={setShareDialogFile}
							onInfo={() => {
								setShowFolderDetails(true)
								setShowMobileDetails(true)
							}}
							onEmptyTrash={handleEmptyTrash}
							createMenu={
								canCreate && !isMobile && <CreateMenu {...createMenuProps} />
							}
							onOpenNav={() => setShowFilter(true)}
							q={filters.q}
							onSearch={(q) => setFilter({ q })}
							activeCount={activeCount}
							onClearFilters={clearAll}
							selectedFiles={multiSelect.getSelectedFiles()}
							onClearSelection={multiSelect.clearSelection}
							fileOps={fileOps}
						/>
					}
				>
					<Fcd.Filter isVisible={showFilter} hide={() => setShowFilter(false)}>
						<Sidebar
							viewMode={viewMode}
							onViewModeChange={handleViewModeChange}
							contextName={contextName}
							room={room}
							allRooms={effectiveScope === 'all'}
							currentFolderId={isRemoteBrowsing ? null : currentFolderId}
							rooms={rooms}
							onRoomChange={handleRoomChange}
							starVersion={starVersion}
						/>
					</Fcd.Filter>
					<Fcd.Content
						width="wide"
						surface="raised"
						header={
							<VBox gap={2}>
								<HandActionBar
									api={api}
									currentFolderId={currentFolderId}
									currentFolderName={
										breadcrumbs.length > 0
											? breadcrumbs[breadcrumbs.length - 1].name
											: undefined
									}
									viewMode={viewMode}
									onRefresh={fileListData.refresh}
									roomChannel={roomChannel}
									contextName={contextName}
									rooms={porch}
								/>
								{fileListData.isOffline && (
									<Alert color="neutral" compact icon={<IcOffline />}>
										{t('Showing cached data — you appear to be offline')}
									</Alert>
								)}
								{viewMode === 'browse' && (
									<FilterBar
										roomName={scopeRoot}
										folderName={
											breadcrumbs.length > 1
												? breadcrumbs[breadcrumbs.length - 1].name
												: undefined
										}
									/>
								)}
							</VBox>
						}
					>
						{isInitialLoading ? (
							<LoadingSpinner fill size="lg" label={t('Loading files...')} />
						) : files.length === 0 &&
							isRoomView &&
							room &&
							!currentFolderId &&
							!searchActive ? (
							<EmptyState
								icon={<Icon as={IcCloud} size="xl" />}
								title={t('No files in ~{{room}} yet.', { room })}
								description={
									audience &&
									t('Files you add here are visible to {{audience}}.', {
										audience
									})
								}
								actions={
									canUpload && (
										<FileButton
											multiple
											color="primary"
											icon={<IcUpload />}
											onFiles={uploadQueue.handleFilesForUpload}
										>
											{t('Upload')}
										</FileButton>
									)
								}
							/>
						) : files.length === 0 && searchActive && !fileListData.permCapped ? (
							<EmptyState
								icon={<Icon as={IcCloud} size="xl" />}
								title={t('No files match these filters')}
								actions={
									<>
										<Button onClick={clearAll}>{t('Clear filters')}</Button>
										{viewMode === 'browse' && effectiveScope === 'folder' && (
											<Button onClick={() => setScope('room')}>
												{t('Search {{place}}', { place: scopeRoot })}
											</Button>
										)}
									</>
								}
							/>
						) : files.length === 0 && !fileListData.permCapped ? (
							<EmptyState
								icon={<Icon as={IcCloud} size="xl" />}
								title={
									isTrashView
										? t('Trash is empty')
										: currentFolderId
											? t('This folder is empty')
											: t('No files found')
								}
								description={
									isTrashView
										? t('Files you delete will appear here')
										: t('Create a new document or upload files to get started')
								}
							/>
						) : displayMode === 'grid' ? (
							<>
								<Grid min="8rem" gap={3} className="p-2" data-file-grid>
									{files.map((file) => (
										<ItemGrid
											key={file.entryId}
											selected={multiSelect.isSelected(file.entryId)}
											file={file}
											isDirty={dirtyDocIds.has(
												`${contextIdTag}:${file.fileId}`
											)}
											onClick={onClickFile}
											onDoubleClick={onDoubleClickFile}
											onContextMenu={onContextMenuFile}
											onInfoClick={auth ? onInfoClick : undefined}
											renameFileId={renameFileId}
											renameFileName={renameFileName}
											fileOps={fileOps}
											viewMode={viewMode}
											showParentChip={showParentChip}
										/>
									))}
								</Grid>
								{loadMoreFooter}
							</>
						) : (
							<>
								<List variant="divided">
									{files.map((file) => (
										<ItemCard
											key={file.entryId}
											selected={multiSelect.isSelected(file.entryId)}
											file={file}
											isDirty={dirtyDocIds.has(
												`${contextIdTag}:${file.fileId}`
											)}
											onClick={onClickFile}
											onDoubleClick={onDoubleClickFile}
											onContextMenu={onContextMenuFile}
											onInfoClick={auth ? onInfoClick : undefined}
											renameFileId={renameFileId}
											renameFileName={renameFileName}
											fileOps={fileOps}
											viewMode={viewMode}
											showParentChip={showParentChip}
										/>
									))}
								</List>
								{loadMoreFooter}
							</>
						)}
						{/* Keeps the last row clear of the mobile FAB */}
						{canCreate && isMobile && (
							<VBox aria-hidden style={{ flex: 'none', height: '5rem' }} />
						)}
					</Fcd.Content>
					<Fcd.Details
						isVisible={!!auth && !!panelFile && (!isMobile || showMobileDetails)}
						hide={() => {
							setShowMobileDetails(false)
							setShowFolderDetails(false)
							multiSelect.clearSelection()
						}}
					>
						{auth && shownFile && (
							<DetailsPanel
								file={shownFile}
								fileOps={fileOps}
								onShare={setShareDialogFile}
								onNavigateToFolder={navigateToFolder}
								ownerScope={remoteScope}
							/>
						)}
					</Fcd.Details>
				</Fcd.Container>
			</DropZone>

			{/* Hidden while UploadProgress (same corner) is showing */}
			{canCreate && isMobile && uploadQueue.queue.length === 0 && (
				<CreateMenu fab {...createMenuProps} />
			)}

			<UploadProgress
				queue={uploadQueue.queue}
				stats={uploadQueue.stats}
				onRemoveItem={uploadQueue.removeItem}
				onClearCompleted={uploadQueue.clearCompleted}
				onClearAll={uploadQueue.clearAll}
			/>

			<ImportChoiceDialog
				pendingConversions={uploadQueue.pendingConversions}
				onUploadAsFile={uploadQueue.uploadAsFile}
				onConvert={uploadQueue.startConversion}
				onDismissAll={uploadQueue.dismissAll}
			/>

			{contextMenuFile && contextMenuPosition && (
				<ContextMenu
					selectedFiles={multiSelect.getSelectedFiles()}
					clickedFile={contextMenuFile}
					position={contextMenuPosition}
					viewMode={viewMode}
					fileOps={fileOps}
					onClose={closeContextMenu}
					onShare={
						isRemoteBrowsing
							? undefined
							: (file) => {
									setShareDialogFile(file)
								}
					}
					isRemoteBrowsing={isRemoteBrowsing}
				/>
			)}

			{shareDialogFile && (
				<ShareDialog
					open={!!shareDialogFile}
					file={shareDialogFile}
					onClose={() => setShareDialogFile(undefined)}
					onPermissionsChanged={fileListData.refresh}
					ownerScope={remoteScope}
				/>
			)}
		</>
	)
}

// vim: ts=4
